// stale-chunk-handler.ts — Catches dynamic import (preload) failures
//
// After a deploy, the old index.html references chunk hashes that no longer
// exist on the server. The only fix is a page reload to pick up the new
// index.html. We allow ONE reload — if chunks still fail after that, it's
// not a stale chunk problem and reloading again won't help (and would cause
// an infinite reload loop that hammers Keycloak).
//
// The reload is gated on the page having had user interaction this navigation
// (`navigator.userActivation.hasBeenActive`). A vite:preloadError can only
// fire when something tried to import a chunk, which in practice means a
// user clicked or navigated — so this gate is almost always satisfied. But
// if it ever isn't (e.g. a passive prefetch in a backgrounded tab), we'd
// rather fail soft than silently reload and kill any media playback.
//
// Before reloading we briefly surface a visible "Updating to the latest
// version…" overlay so the user knows what's about to happen instead of
// seeing a sudden page flash.

import { reloadForStaleBuild } from "./utils/reloadForStaleBuild";

const NOTICE_DURATION_MS = 800;

function showUpdatingNotice(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById("stale-chunk-notice")) return;

  const overlay = document.createElement("div");
  overlay.id = "stale-chunk-notice";
  overlay.setAttribute("role", "status");
  overlay.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "right:0",
    "z-index:2147483647",
    "padding:14px 16px",
    "background:#1f2937",
    "color:#ffffff",
    "text-align:center",
    "font:600 14px/1.4 system-ui,-apple-system,sans-serif",
    "box-shadow:0 2px 8px rgba(0,0,0,0.25)",
  ].join(";");
  overlay.textContent = "Updating to the latest version…";
  document.body.appendChild(overlay);
}

window.addEventListener("vite:preloadError", (event: Event) => {
  const e = event as CustomEvent;
  event.preventDefault();

  const hasActivation =
    typeof navigator !== "undefined" &&
    typeof navigator.userActivation !== "undefined" &&
    navigator.userActivation.hasBeenActive === true;
  if (!hasActivation) {
    // Passive failure in a tab the user isn't touching — let it fall
    // through to the error boundary's fallback UI instead of silently
    // reloading. They'll see the prompt when they return.
    return;
  }

  showUpdatingNotice();

  // Small delay so the overlay actually renders before we reload.
  setTimeout(() => {
    reloadForStaleBuild({
      key: "stale_chunk_reload",
      reason: "stale_chunk",
      context: {
        message: e.detail?.message || "Dynamic import failed",
      },
    });
    // If the breaker has already tripped for this build, control returns
    // here without a reload happening. The error falls through to
    // WidgetErrorBoundary / AppErrorBoundary as a last resort.
  }, NOTICE_DURATION_MS);
});
